// /prompts — user's custom director prompts.

import { useEffect, useState } from 'react';
import { ApiError, customPrompts, type CustomPrompt } from '../api.js';

export function PromptsPage() {
  const [list, setList] = useState<CustomPrompt[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; name: string; content: string; is_default: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const r = await customPrompts.list();
      setList(r.prompts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
      setList([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleSave() {
    if (!editing) return;
    setSubmitting(true);
    setError(null);
    try {
      if (editing.id) {
        await customPrompts.patch(editing.id, {
          name: editing.name,
          content: editing.content,
          is_default: editing.is_default,
        });
      } else {
        await customPrompts.create({
          name: editing.name,
          content: editing.content,
          is_default: editing.is_default,
        });
      }
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete prompt "${name}"?`)) return;
    try {
      await customPrompts.remove(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Custom prompts</h1>
          <p className="text-muted text-sm">
            Override the default director prompt. Pin one as default and it'll be used for every direct.
          </p>
        </div>
        <button
          onClick={() => setEditing({ id: null, name: '', content: '', is_default: false })}
          className="bg-ink text-parchment px-4 py-2 rounded-md hover:bg-ink/90 transition-colors text-sm"
        >
          New prompt
        </button>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {editing && (
        <div className="border border-soft rounded-lg bg-white px-5 py-5 mb-4">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl">{editing.id ? 'Edit prompt' : 'New prompt'}</h2>
            <button onClick={() => setEditing(null)} className="text-xs text-muted hover:text-ink">
              Cancel
            </button>
          </div>
          <div className="space-y-3">
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="Prompt name"
              maxLength={200}
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded"
            />
            <textarea
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              placeholder="The system prompt the director uses…"
              rows={14}
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded font-mono text-sm"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.is_default}
                onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
              />
              <span>Use as my default for new projects</span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={submitting || !editing.name.trim() || editing.content.length < 20}
                className="bg-accent text-parchment px-4 py-2 rounded text-sm hover:bg-accent/90 disabled:opacity-40 transition-colors"
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {list === null ? (
        <p className="text-muted text-sm">loading…</p>
      ) : list.length === 0 ? (
        <div className="border border-soft rounded-lg p-12 text-center">
          <p className="text-muted">No custom prompts yet.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((p) => (
            <li key={p.id} className="border border-soft rounded-lg px-5 py-4 bg-white">
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-serif text-lg truncate">{p.name}</h2>
                    {p.is_default && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-accent/10 text-accent rounded">
                        default
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {p.usage_count.toLocaleString()} uses · updated {new Date(p.updated_at).toLocaleDateString()}
                  </div>
                  <p className="text-xs text-muted/80 mt-2 font-mono line-clamp-2">
                    {p.content.slice(0, 200)}
                    {p.content.length > 200 ? '…' : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setEditing({ id: p.id, name: p.name, content: p.content, is_default: p.is_default })}
                    className="text-xs text-muted hover:text-ink px-2 py-1"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(p.id, p.name)}
                    className="text-xs text-muted hover:text-accent px-2 py-1"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
